import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { User, Loader2, Phone, Mail, Camera, X, ArrowLeftRight, GraduationCap } from "lucide-react";
import type { Minister } from "@/models/minister";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { Degree } from "@/models/degree";
import type { MinisterFormData, MinisterFormErrors } from "../../types";
import { useMinisterPhoto } from "../../hooks/useMinisterPhoto";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

interface MinisterFormProps {
  formData: MinisterFormData;
  errors: MinisterFormErrors;
  touched: Partial<Record<keyof MinisterFormData, boolean>>;
  onFieldChange: <K extends keyof MinisterFormData>(field: K, value: MinisterFormData[K]) => void;
  onFieldBlur: (field: keyof MinisterFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  districts: District[];
  regions: Region[];
  degrees: Degree[];
  photoPreview: string | null;
  onPhotoSelect: (file: File | null) => void;
  shouldDeletePhoto?: boolean;
  isSubmitting: boolean;
  isReadOnly: boolean;
  existingMinister?: Minister;
  hasFormChanged?: boolean;
  onStatusToggle?: (checked: boolean) => void;
  onDegreeChangeRequest?: () => void;
  onTransferRequest?: () => void;
}

/**
 * Organism: Minister form with validation and photo upload
 * Displays all minister fields with proper validation and cascading selects
 */
export function MinisterForm({
  formData,
  errors,
  touched,
  onFieldChange,
  onFieldBlur,
  onSubmit,
  onCancel,
  districts,
  regions,
  degrees,
  photoPreview,
  onPhotoSelect,
  shouldDeletePhoto = false,
  isSubmitting,
  isReadOnly,
  existingMinister,
  hasFormChanged = false,
  onStatusToggle,
  onDegreeChangeRequest,
  onTransferRequest,
}: MinisterFormProps) {
  // State for photo upload UX
  const [isHoveringPhoto, setIsHoveringPhoto] = useState(false);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [photoMessage, setPhotoMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing photo from Supabase Storage if editing
  const { photoUrl, isLoading: isPhotoLoading } = useMinisterPhoto(existingMinister?.profile_picture_filename);

  // Get available regions based on selected district
  const availableRegions = regions.filter(
    (r) => r.district_id === formData.district_id
  );

  // Reset region when district changes
  useEffect(() => {
    if (formData.district_id && !availableRegions.find((r) => r.id === formData.region_id)) {
      onFieldChange("region_id", "");
    }
  }, [formData.district_id, formData.region_id, availableRegions, onFieldChange]);

  // Photo validation constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
  const MIN_WIDTH = 200;
  const MIN_HEIGHT = 200;

  // Validate photo file
  const validatePhoto = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Solo se permiten archivos JPG, JPEG o PNG';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'El archivo no debe superar los 5MB';
    }
    return null;
  };

  // Validate photo dimensions
  const validatePhotoDimensions = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
          resolve(`La imagen debe ser al menos ${MIN_WIDTH}x${MIN_HEIGHT}px`);
        } else {
          resolve(null);
        }
      };
      img.onerror = () => resolve('No se pudo cargar la imagen');
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle photo selection from file input or drag-drop
  const handlePhotoFile = async (file: File) => {
    // Validate file type and size
    const validationError = validatePhoto(file);
    if (validationError) {
      setPhotoMessage(validationError);
      setTimeout(() => setPhotoMessage(null), 5000);
      return;
    }

    // Validate dimensions
    const dimensionError = await validatePhotoDimensions(file);
    if (dimensionError) {
      setPhotoMessage(dimensionError);
      setTimeout(() => setPhotoMessage(null), 5000);
      return;
    }

    // Photo is valid
    onPhotoSelect(file);
    setPhotoMessage('Foto seleccionada correctamente');
    setTimeout(() => setPhotoMessage(null), 3000);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handlePhotoFile(file);
    }
  };

  const handlePhotoClick = () => {
    if (!isReadOnly) {
      fileInputRef.current?.click();
    }
  };

  const handleCancelPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPhotoSelect(null);
    setPhotoMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isReadOnly) {
      setIsDraggingPhoto(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide overlay if leaving the drop zone container itself
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDraggingPhoto(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhoto(false);
    
    if (isReadOnly) return;

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await handlePhotoFile(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  // Check if all required fields are filled (for CREATE mode)
  const areRequiredFieldsFilled = () => {
    return !!(
      formData.adb_code &&
      formData.first_name &&
      formData.last_name &&
      formData.dni &&
      formData.district_id &&
      formData.region_id &&
      formData.degree_id
    );
  };

  // Determine if submit button should be disabled
  const isSubmitDisabled = () => {
    if (isSubmitting) return true;
    if (existingMinister) {
      // UPDATE mode: disable if no changes made
      return !hasFormChanged;
    } else {
      // CREATE mode: disable if required fields not filled
      return !areRequiredFieldsFilled();
    }
  };

  // Generate initials for fallback
  const initials = `${formData.first_name.charAt(0)}${formData.last_name.charAt(0)}`.toUpperCase();

  // Determine which photo to show: new preview > existing Supabase photo > placeholder
  // Hide photo if user marked it for deletion
  const displayPhotoUrl = shouldDeletePhoto ? null : (photoPreview || photoUrl);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Lifecycle actions bar — admin edit mode only */}
      {!isReadOnly && existingMinister && (onDegreeChangeRequest || onTransferRequest) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-4 py-3">
          <span className="mr-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">Acciones</span>
          {onTransferRequest && (
            <Button type="button" variant="outline" size="sm" onClick={onTransferRequest}>
              <ArrowLeftRight className="mr-2 size-4" />
              Transferir
            </Button>
          )}
          {onDegreeChangeRequest && (
            <Button type="button" variant="outline" size="sm" onClick={onDegreeChangeRequest}>
              <GraduationCap className="mr-2 size-4" />
              Cambiar Grado
            </Button>
          )}
        </div>
      )}

      {/* Two column layout: Photo on left, Form fields on right */}
      <div className="flex w-full flex-col gap-6 sm:flex-row">
        {/* Photo column */}
        <div className="flex flex-1 flex-col items-center justify-start">
          {/* Profile photo with hover overlay and drag-drop */}
          <div 
            className="group relative m-4 h-72 w-60 cursor-pointer overflow-hidden rounded-md"
            onMouseEnter={() => !isReadOnly && setIsHoveringPhoto(true)}
            onMouseLeave={() => setIsHoveringPhoto(false)}
            onClick={handlePhotoClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isPhotoLoading ? (
              <div className="flex h-72 w-60 items-center justify-center bg-muted">
                <Loader2 className="size-12 animate-spin text-muted-foreground" />
              </div>
            ) : displayPhotoUrl ? (
              <>
                <img 
                  src={displayPhotoUrl} 
                  alt="Foto de perfil" 
                  className="h-72 w-60 object-cover"
                />
                {/* Hover overlay for edit mode */}
                {!isReadOnly && (isHoveringPhoto || isDraggingPhoto) && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 transition-opacity">
                    <Camera className="mb-2 size-10 text-white" />
                    <span className="text-sm font-medium text-white">
                      {isDraggingPhoto ? 'Soltar foto aquí' : 'Cambiar foto'}
                    </span>
                    <span className="mt-1 text-xs text-white/80">
                      {isDraggingPhoto ? '' : 'o arrastrar y soltar'}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-72 w-60 items-center justify-center bg-muted">
                {initials ? (
                  <span className="text-6xl font-semibold text-muted-foreground">
                    {initials}
                  </span>
                ) : (
                  <User className="size-24 text-muted-foreground" />
                )}
                {/* Hover overlay for empty photo */}
                {!isReadOnly && (isHoveringPhoto || isDraggingPhoto) && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 transition-opacity">
                    <Camera className="mb-2 size-10 text-white" />
                    <span className="text-sm font-medium text-white">
                      {isDraggingPhoto ? 'Soltar foto aquí' : 'Subir foto'}
                    </span>
                    <span className="mt-1 text-xs text-white/80">
                      {isDraggingPhoto ? '' : 'o arrastrar y soltar'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Cancel button for new photo preview OR clear existing photo */}
            {!isReadOnly && displayPhotoUrl && (
              <button
                type="button"
                onClick={handleCancelPhoto}
                className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
                title={photoPreview ? "Cancelar foto nueva" : "Eliminar foto actual"}
              >
                <X className="size-4" />
              </button>
            )}
          </div>

          {/* Hidden file input */}
          {!isReadOnly && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpg,image/jpeg"
              onChange={handlePhotoChange}
              className="hidden"
            />
          )}

          {/* Photo selection message */}
          {!isReadOnly && photoMessage && (
            <div className="mx-4 w-60">
              <p className={`text-center text-sm ${
                photoMessage.includes('correctamente') 
                  ? 'text-green-600' 
                  : 'text-destructive'
              }`}>
                {photoMessage}
              </p>
            </div>
          )}

          {/* Minister Key Info Card */}
          {existingMinister && (
            <Card className="mx-4 w-60">
              <CardContent className="space-y-3 pt-4">
                {/* Full Name */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nombre Completo</p>
                  <p className="text-lg font-semibold">{existingMinister.full_name}</p>
                </div>
                {/* ADB Code */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Código ADB</p>
                  <p className="text-base font-semibold">{existingMinister.adb_code}</p>
                </div>

                {/* Status Badge */}
                <div>
                  <Badge variant={existingMinister.is_active ? "default" : "secondary"}>
                    {existingMinister.is_active ? "Activo" : "Inactivo"}
                  </Badge>
                </div>

                {/* Status toggle — only shown in edit mode when onStatusToggle is provided (admin) */}
                {!isReadOnly && onStatusToggle && (
                  <div className="space-y-2">
                    <Label htmlFor="is_active" className="text-sm font-medium text-muted-foreground">
                      Cambiar Estado
                    </Label>
                    <div className="flex items-center gap-3">
                      <Switch
                        id="is_active"
                        checked={existingMinister?.is_active ?? formData.is_active}
                        onCheckedChange={onStatusToggle}
                      />
                      <span className="text-sm font-medium">
                        {(existingMinister?.is_active ?? formData.is_active) ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                  </div>
                )}

                {/* Degree */}
                {existingMinister.degree && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Grado Ministerial</p>
                    <p className="text-base">{existingMinister.degree.name}</p>
                  </div>
                )}

                {/* District */}
                {existingMinister.district && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Distrito</p>
                    <p className="text-base">{existingMinister.district.name}</p>
                  </div>
                )}

                {/* Region */}
                {existingMinister.region && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Región</p>
                    <p className="text-base">{existingMinister.region.name}</p>
                  </div>
                )}

                {/* Phone */}
                {existingMinister.cell_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="size-4 text-muted-foreground" />
                    <p className="text-base">{existingMinister.cell_phone}</p>
                  </div>
                )}

                {/* Email */}
                {existingMinister.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="size-4 text-muted-foreground" />
                    <p className="truncate text-base">{existingMinister.email}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Form fields column */}
        <div className="flex-1 space-y-4 px-4">
          {/* Required fields section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Información requerida</h3>
        
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* ADB Code */}
          <div className="space-y-2">
            <Label htmlFor="adb_code">
              Código ADB <span className="text-destructive">*</span>
            </Label>
            <Input
              id="adb_code"
              value={formData.adb_code}
              onChange={(e) => onFieldChange("adb_code", e.target.value)}
              onBlur={() => onFieldBlur("adb_code")}
              readOnly={isReadOnly}
              placeholder="12-123"
              className={errors.adb_code && touched.adb_code ? "border-destructive" : ""}
            />
            {errors.adb_code && touched.adb_code && (
              <p className="text-xs text-destructive">{errors.adb_code}</p>
            )}
          </div>

          {/* DNI */}
          <div className="space-y-2">
            <Label htmlFor="dni">
              Cédula <span className="text-destructive">*</span>
            </Label>
            <Input
              id="dni"
              value={formData.dni}
              onChange={(e) => onFieldChange("dni", e.target.value)}
              onBlur={() => onFieldBlur("dni")}
              readOnly={isReadOnly}
              placeholder="12345678"
              className={errors.dni && touched.dni ? "border-destructive" : ""}
            />
            {errors.dni && touched.dni && (
              <p className="text-xs text-destructive">{errors.dni}</p>
            )}
          </div>

          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="first_name">
              Nombres <span className="text-destructive">*</span>
            </Label>
            <Input
              id="first_name"
              value={formData.first_name}
              onChange={(e) => onFieldChange("first_name", e.target.value)}
              onBlur={() => onFieldBlur("first_name")}
              readOnly={isReadOnly}
              placeholder="Juan Alberto"
              className={errors.first_name && touched.first_name ? "border-destructive" : ""}
            />
            {errors.first_name && touched.first_name && (
              <p className="text-xs text-destructive">{errors.first_name}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="last_name">
              Apellido Paterno <span className="text-destructive">*</span>
            </Label>
            <Input
              id="last_name"
              value={formData.last_name}
              onChange={(e) => onFieldChange("last_name", e.target.value)}
              onBlur={() => onFieldBlur("last_name")}
              readOnly={isReadOnly}
              placeholder="Pérez"
              className={errors.last_name && touched.last_name ? "border-destructive" : ""}
            />
            {errors.last_name && touched.last_name && (
              <p className="text-xs text-destructive">{errors.last_name}</p>
            )}
          </div>

          {/* Second Last Name */}
          <div className="space-y-2">
            <Label htmlFor="second_last_name">Apellido Materno</Label>
            <Input
              id="second_last_name"
              value={formData.second_last_name}
              onChange={(e) => onFieldChange("second_last_name", e.target.value)}
              onBlur={() => onFieldBlur("second_last_name")}
              readOnly={isReadOnly}
              placeholder="García"
            />
          </div>

          {/* Marriage Last Name */}
          <div className="space-y-2">
            <Label htmlFor="marriage_last_name">Apellido de Casada</Label>
            <Input
              id="marriage_last_name"
              value={formData.marriage_last_name}
              onChange={(e) => onFieldChange("marriage_last_name", e.target.value)}
              onBlur={() => onFieldBlur("marriage_last_name")}
              readOnly={isReadOnly}
              placeholder="de López"
            />
          </div>

          {/* District */}
          <div className="space-y-2">
            <Label htmlFor="district_id">
              Filial {!existingMinister && <span className="text-destructive">*</span>}
            </Label>
            {existingMinister ? (
              <div className="flex items-center rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <span>{existingMinister.district?.name ?? "Sin distrito"}</span>
              </div>
            ) : (
              <>
                <Select
                  value={formData.district_id}
                  onValueChange={(value) => onFieldChange("district_id", value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger
                    id="district_id"
                    className={errors.district_id && touched.district_id ? "border-destructive" : ""}
                  >
                    <SelectValue placeholder="Seleccione un distrito" />
                  </SelectTrigger>
                  <SelectContent>
                    {districts.map((district) => (
                      <SelectItem key={district.id} value={district.id}>
                        {district.name || "Sin nombre"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.district_id && touched.district_id && (
                  <p className="text-xs text-destructive">{errors.district_id}</p>
                )}
              </>
            )}
          </div>

          {/* Region */}
          <div className="space-y-2">
            <Label htmlFor="region_id">
              Región {!existingMinister && <span className="text-destructive">*</span>}
            </Label>
            {existingMinister ? (
              <div className="flex items-center rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <span>{existingMinister.region?.name ?? "Sin región"}</span>
              </div>
            ) : (
              <>
                <Select
                  value={formData.region_id}
                  onValueChange={(value) => onFieldChange("region_id", value)}
                  disabled={isReadOnly || !formData.district_id}
                >
                  <SelectTrigger
                    id="region_id"
                    className={errors.region_id && touched.region_id ? "border-destructive" : ""}
                  >
                    <SelectValue placeholder={formData.district_id ? "Seleccione una región" : "Seleccione primero un distrito"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRegions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.name || "Sin nombre"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.region_id && touched.region_id && (
                  <p className="text-xs text-destructive">{errors.region_id}</p>
                )}
              </>
            )}
          </div>

          {/* Degree */}
          <div className="space-y-2">
            <Label htmlFor="degree_id">
              Grado {!existingMinister && <span className="text-destructive">*</span>}
            </Label>
            {existingMinister ? (
              <div className="flex items-center rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <span>{existingMinister.degree?.name ?? "Sin grado"}</span>
              </div>
            ) : (
              <>
                <Select
                  value={formData.degree_id}
                  onValueChange={(value) => onFieldChange("degree_id", value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger
                    id="degree_id"
                    className={errors.degree_id && touched.degree_id ? "border-destructive" : ""}
                  >
                    <SelectValue placeholder="Seleccione un grado" />
                  </SelectTrigger>
                  <SelectContent>
                    {degrees.map((degree) => (
                      <SelectItem key={degree.id} value={degree.id}>
                        {degree.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.degree_id && touched.degree_id && (
                  <p className="text-xs text-destructive">{errors.degree_id}</p>
                )}
              </>
            )}
          </div>

          {/* Position */}
          <div className="space-y-2">
            <Label htmlFor="position">Cargo</Label>
            <Input
              id="position"
              value={formData.position}
              onChange={(e) => onFieldChange("position", e.target.value)}
              onBlur={() => onFieldBlur("position")}
              readOnly={isReadOnly}
              placeholder="Pastor"
            />
          </div>
        </div>
      </div>

      {/* Contact information section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">Información de contacto</h3>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => onFieldChange("email", e.target.value)}
              onBlur={() => onFieldBlur("email")}
              readOnly={isReadOnly}
              placeholder="ministro@example.com"
              className={errors.email && touched.email ? "border-destructive" : ""}
            />
            {errors.email && touched.email && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>

          {/* Cell Phone */}
          <div className="space-y-2">
            <Label htmlFor="cell_phone">Teléfono Celular</Label>
            <Input
              id="cell_phone"
              value={formData.cell_phone}
              onChange={(e) => onFieldChange("cell_phone", e.target.value)}
              onBlur={() => onFieldBlur("cell_phone")}
              readOnly={isReadOnly}
              placeholder="70123456"
            />
          </div>

          {/* Office Phone */}
          <div className="space-y-2">
            <Label htmlFor="office_phone">Teléfono Oficina</Label>
            <Input
              id="office_phone"
              value={formData.office_phone}
              onChange={(e) => onFieldChange("office_phone", e.target.value)}
              onBlur={() => onFieldBlur("office_phone")}
              readOnly={isReadOnly}
              placeholder="4123456"
            />
          </div>
        </div>
      </div>

      {/* Address section */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">Dirección</h3>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Address */}
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => onFieldChange("address", e.target.value)}
              onBlur={() => onFieldBlur("address")}
              readOnly={isReadOnly}
              placeholder="Calle Principal #123"
            />
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="address_city">Ciudad</Label>
            <Input
              id="address_city"
              value={formData.address_city}
              onChange={(e) => onFieldChange("address_city", e.target.value)}
              onBlur={() => onFieldBlur("address_city")}
              readOnly={isReadOnly}
              placeholder="La Paz"
            />
          </div>

          {/* Zone */}
          <div className="space-y-2">
            <Label htmlFor="address_zone">Zona</Label>
            <Input
              id="address_zone"
              value={formData.address_zone}
              onChange={(e) => onFieldChange("address_zone", e.target.value)}
              onBlur={() => onFieldBlur("address_zone")}
              readOnly={isReadOnly}
              placeholder="Zona Central"
            />
          </div>
        </div>
      </div>

          {/* End of form fields column */}
        </div>
      </div>

      {/* Form actions */}
      {!isReadOnly && (
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitDisabled()}>
            {isSubmitting ? "Guardando..." : existingMinister ? "Actualizar" : "Crear"}
          </Button>
        </div>
      )}
    </form>
  );
}
